import { useRef, useState } from "react";
import { CheckCircle2, Lock, AlertTriangle } from "lucide-react";

interface MandatoryVideoProps {
  src: string;
  onComplete: () => void;
}

type Parsed =
  | { kind: "file"; url: string }
  | { kind: "embed"; url: string }
  | { kind: "invalid" };

// Figure out how to play whatever URL was configured. Direct media files use the native
// <video> (which lets us enforce watch-to-the-end). YouTube / Vimeo / Google Drive links
// are page URLs a <video> can't load, so we embed them in an <iframe> instead.
const parseSource = (raw: string): Parsed => {
  const src = (raw || "").trim();
  if (!src) return { kind: "invalid" };

  // Direct media file (optionally with a query string).
  if (/\.(mp4|webm|ogg|ogv|mov|m4v)(\?.*)?$/i.test(src)) return { kind: "file", url: src };

  // YouTube
  const yt = src.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { kind: "embed", url: `https://www.youtube.com/embed/${yt[1]}?rel=0&modestbranding=1` };

  // Vimeo
  const vm = src.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { kind: "embed", url: `https://player.vimeo.com/video/${vm[1]}` };

  // Google Drive
  const gd = src.match(/drive\.google\.com\/file\/d\/([\w-]+)/);
  if (gd) return { kind: "embed", url: `https://drive.google.com/file/d/${gd[1]}/preview` };

  // Unknown http(s) URL — try to embed it (some CDNs serve embeddable players).
  if (/^https?:\/\//i.test(src)) return { kind: "embed", url: src };

  return { kind: "invalid" };
};

// A video that must be watched to the end before it reports completion. Seeking ahead
// of the furthest-watched point is blocked, so students can't skip. Rewatching (seeking
// backward) is allowed. For embedded players (YouTube/Vimeo/Drive) end-detection isn't
// available, so the student confirms completion with an explicit checkbox instead.
const MandatoryVideo = ({ src, onComplete }: MandatoryVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const maxTimeRef = useRef(0);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(false);

  const parsed = parseSource(src);

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.currentTime > maxTimeRef.current) maxTimeRef.current = v.currentTime;
  };

  const handleSeeking = () => {
    const v = videoRef.current;
    if (!v) return;
    // Clamp any forward jump back to the furthest point actually watched (small tolerance).
    if (v.currentTime > maxTimeRef.current + 0.5) v.currentTime = maxTimeRef.current;
  };

  const markDone = () => {
    if (done) return;
    setDone(true);
    onComplete();
  };

  if (parsed.kind === "invalid") {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 py-10 text-center">
        <AlertTriangle className="h-6 w-6 text-destructive" />
        <p className="text-sm font-medium text-destructive">Briefing video isn’t configured correctly.</p>
        <p className="text-xs text-muted-foreground">Ask an admin to set a valid video link in Settings.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {parsed.kind === "file" ? (
        error ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 aspect-video text-center">
            <AlertTriangle className="h-6 w-6 text-destructive" />
            <p className="text-sm font-medium text-destructive">This video couldn’t be loaded.</p>
            <p className="text-xs text-muted-foreground px-6">The link may be broken or blocked. An admin can update it in Settings.</p>
          </div>
        ) : (
          <video
            ref={videoRef}
            src={parsed.url}
            controls
            controlsList="nodownload noplaybackrate"
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()}
            onTimeUpdate={handleTimeUpdate}
            onSeeking={handleSeeking}
            onEnded={markDone}
            onError={() => setError(true)}
            className="w-full rounded-lg bg-black aspect-video"
          />
        )
      ) : (
        <div className="w-full rounded-lg bg-black aspect-video overflow-hidden">
          <iframe
            src={parsed.url}
            title="Mandatory Briefing"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full border-0"
          />
        </div>
      )}

      {/* Direct files auto-complete on end; embeds need an explicit confirmation. */}
      {parsed.kind === "embed" && !error && (
        <label className={`flex items-center gap-2 text-xs font-medium cursor-pointer ${done ? "text-green-600" : "text-muted-foreground"}`}>
          <input type="checkbox" checked={done} disabled={done} onChange={markDone} className="h-3.5 w-3.5 accent-primary" />
          I have watched the full briefing video.
        </label>
      )}

      {(parsed.kind === "file" && !error) && (
        <div className={`flex items-center gap-2 text-xs font-medium ${done ? "text-green-600" : "text-muted-foreground"}`}>
          {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
          {done
            ? "Video completed — your exam is now unlocked."
            : "Watch the full video to unlock your exam. Skipping ahead is disabled."}
        </div>
      )}

      {done && parsed.kind === "embed" && (
        <div className="flex items-center gap-2 text-xs font-medium text-green-600">
          <CheckCircle2 className="h-3.5 w-3.5" /> Video confirmed — your exam is now unlocked.
        </div>
      )}
    </div>
  );
};

export default MandatoryVideo;
